import { useEffect, useState } from "react";
import api from "@/lib/api";
import { guessType, CATEGORIES, categoryById } from "@/lib/movemap";
import { BTN_PRIMARY, ICON_BTN, INPUT } from "@/lib/styles";
import { X, TrendingUp, TrendingDown, Plus, Check, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

const uid = () => "mv_" + Math.random().toString(36).slice(2, 10);
const todayStr = () => new Date().toISOString().slice(0, 10);

export function MovementForm({ initial, onSave, onCancel }) {
  const [concepto, setConcepto] = useState(initial?.concepto || "");
  const [descripcion, setDescripcion] = useState(initial?.descripcion || "");
  const [categoria, setCategoria] = useState(initial?.categoria || "");
  const [monto, setMonto] = useState(initial?.monto ?? "");
  const [moneda, setMoneda] = useState(initial?.moneda || "USD");
  const [tipo, setTipo] = useState(initial?.tipo || "");
  const [fecha, setFecha] = useState(initial?.fecha ? initial.fecha.slice(0, 10) : todayStr());
  const [touchedTipo, setTouchedTipo] = useState(!!initial?.tipo || !!initial?.categoria);
  const [customCats, setCustomCats] = useState([]);
  const [addingCat, setAddingCat] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState("");
  const [newCatTipo, setNewCatTipo] = useState("egreso");
  const [savingCat, setSavingCat] = useState(false);
  const [montoDestino, setMontoDestino] = useState(initial?.montoDestino ?? "");
  const [monedaDestino, setMonedaDestino] = useState(initial?.monedaDestino || "VES");

  useEffect(() => {
    api.get("/finance/movement-categories").then(({ data }) => setCustomCats(data.categories || [])).catch(() => {});
  }, []);

  const allCats = [...CATEGORIES, ...customCats];

  useEffect(() => {
    if (touchedTipo || categoria) return;
    const g = guessType(concepto);
    if (g) setTipo(g);
  }, [concepto, touchedTipo, categoria]);

  const pickCategory = (id) => {
    setCategoria(id);
    const cat = allCats.find((c) => c.id === id) || categoryById(id);
    if (cat) { setTipo(cat.tipo); setTouchedTipo(true); }
  };

  const saveNewCat = async () => {
    const label = newCatLabel.trim();
    if (!label) return toast.error("Escribe el nombre de la categoría.");
    setSavingCat(true);
    try {
      const { data } = await api.post("/finance/movement-categories", { label, tipo: newCatTipo });
      setCustomCats(data.categories || []);
      const created = (data.categories || []).find((c) => c.label.toLowerCase() === label.toLowerCase() && c.tipo === newCatTipo);
      if (created) pickCategory(created.id);
      setAddingCat(false); setNewCatLabel("");
      toast.success("Categoría guardada para futuros movimientos");
    } catch (e) { toast.error("No se pudo crear la categoría."); }
    finally { setSavingCat(false); }
  };

  const guessed = guessType(concepto);
  const ambiguous = !tipo;

  const submit = () => {
    if (tipo === "cambio") {
      const from = Number(monto), to = Number(montoDestino);
      if (!from || from <= 0) return toast.error("Escribe cuánto entregas.");
      if (!to || to <= 0) return toast.error("Escribe cuánto recibes.");
      return onSave({
        id: initial?.id || uid(),
        concepto: concepto.trim() || "Cambio de moneda",
        descripcion: descripcion.trim(),
        categoria: "",
        monto: from, moneda,
        montoDestino: to, monedaDestino,
        tipo: "cambio", fecha,
      });
    }
    if (!concepto.trim()) return toast.error("Escribe un concepto.");
    const amt = Number(monto);
    if (!amt || amt <= 0) return toast.error("Escribe un monto válido.");
    if (!tipo) return toast.error("Indica si es un ingreso o un egreso (o elige una categoría).");
    onSave({
      id: initial?.id || uid(),
      concepto: concepto.trim(),
      descripcion: descripcion.trim(),
      categoria,
      monto: amt, moneda, tipo, fecha,
    });
  };

  const ingresoCats = allCats.filter((c) => c.tipo === "ingreso");
  const egresoCats = allCats.filter((c) => c.tipo === "egreso");

  return (
    <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel} data-testid="movement-form">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-medium text-lg">{initial ? "Editar movimiento" : "Nuevo movimiento"}</h3>
          <button onClick={onCancel} className={ICON_BTN} data-testid="movement-close"><X size={16} /></button>
        </div>

        <label className="font-mono2 text-[11px] text-slate-500 dark:text-slate-400">Concepto</label>
        <input className={`${INPUT} mt-1 mb-3`} value={concepto} onChange={(e) => setConcepto(e.target.value)} placeholder="Ej: Sueldo julio, Pago alquiler..." data-testid="movement-concepto" autoFocus />

        <label className="font-mono2 text-[11px] text-slate-500 dark:text-slate-400">Categoría (clasificación)</label>
        <select className={`${INPUT} mt-1 mb-1`} value={categoria} onChange={(e) => pickCategory(e.target.value)} data-testid="movement-categoria">
          <option value="">— Sin categoría (se deduce del concepto) —</option>
          <optgroup label="Ingresos">
            {ingresoCats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </optgroup>
          <optgroup label="Egresos">
            {egresoCats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </optgroup>
        </select>
        {addingCat ? (
          <div className="flex items-center gap-2 mt-1 mb-3 p-2 rounded-lg border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50 dark:bg-indigo-500/10" data-testid="movement-newcat-box">
            <input className={`${INPUT} flex-1`} value={newCatLabel} onChange={(e) => setNewCatLabel(e.target.value)} placeholder="Nueva categoría (ej. Mascotas)" data-testid="movement-newcat-label" autoFocus onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveNewCat(); } }} />
            <select className={`${INPUT} w-24`} value={newCatTipo} onChange={(e) => setNewCatTipo(e.target.value)} data-testid="movement-newcat-tipo">
              <option value="egreso">Egreso</option>
              <option value="ingreso">Ingreso</option>
            </select>
            <button type="button" onClick={saveNewCat} disabled={savingCat} className={`${ICON_BTN} text-emerald-600`} title="Guardar categoría" data-testid="movement-newcat-save"><Check size={16} /></button>
            <button type="button" onClick={() => { setAddingCat(false); setNewCatLabel(""); }} className={ICON_BTN} data-testid="movement-newcat-cancel"><X size={16} /></button>
          </div>
        ) : (
          <button type="button" onClick={() => setAddingCat(true)} className="flex items-center gap-1 text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline mb-3 mt-0.5" data-testid="movement-newcat-open">
            <Plus size={12} /> Crear categoría nueva (se guarda para futuros movimientos)
          </button>
        )}

        <label className="font-mono2 text-[11px] text-slate-500 dark:text-slate-400">Descripción (opcional)</label>
        <textarea className={`${INPUT} mt-1 mb-3 resize-none`} rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Detalle: ¿en qué se hizo este movimiento?" data-testid="movement-descripcion" />

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="font-mono2 text-[11px] text-slate-500 dark:text-slate-400">{tipo === "cambio" ? "Entregas" : "Monto"}</label>
            <input type="number" className={`${INPUT} mt-1`} value={monto} onChange={(e) => setMonto(e.target.value)} placeholder="0.00" data-testid="movement-monto" />
          </div>
          <div>
            <label className="font-mono2 text-[11px] text-slate-500 dark:text-slate-400">Moneda</label>
            <select className={`${INPUT} mt-1`} value={moneda} onChange={(e) => setMoneda(e.target.value)} data-testid="movement-moneda">
              <option value="USD">$ (Dólar)</option>
              <option value="VES">Bs (Bolívar)</option>
            </select>
          </div>
        </div>

        <label className="font-mono2 text-[11px] text-slate-500 dark:text-slate-400">Tipo de movimiento</label>
        <div className="grid grid-cols-2 gap-3 mt-1 mb-2">
          <button type="button" onClick={() => { setTipo("ingreso"); setTouchedTipo(true); }} className={`inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium border transition ${tipo === "ingreso" ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-200 dark:border-slate-700 text-emerald-600 dark:text-emerald-400"}`} data-testid="movement-tipo-ingreso">
            <TrendingUp size={16} /> Ingreso
          </button>
          <button type="button" onClick={() => { setTipo("egreso"); setTouchedTipo(true); }} className={`inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium border transition ${tipo === "egreso" ? "bg-rose-500 border-rose-500 text-white" : "border-slate-200 dark:border-slate-700 text-rose-600 dark:text-rose-400"}`} data-testid="movement-tipo-egreso">
            <TrendingDown size={16} /> Egreso
          </button>
        </div>
        <button type="button" onClick={() => { setTipo("cambio"); setTouchedTipo(true); }} className={`w-full inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium border transition mb-1 ${tipo === "cambio" ? "bg-sky-500 border-sky-500 text-white" : "border-slate-200 dark:border-slate-700 text-sky-600 dark:text-sky-400"}`} data-testid="movement-tipo-cambio">
          <ArrowLeftRight size={16} /> Cambio de moneda (no cuenta como ingreso/egreso)
        </button>

        {tipo === "cambio" ? (
          <div className="grid grid-cols-2 gap-3 mt-2 mb-3 p-2 rounded-lg border border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10">
            <div>
              <label className="font-mono2 text-[11px] text-slate-500 dark:text-slate-400">Recibes</label>
              <input type="number" className={`${INPUT} mt-1`} value={montoDestino} onChange={(e) => setMontoDestino(e.target.value)} placeholder="0.00" data-testid="movement-monto-destino" />
            </div>
            <div>
              <label className="font-mono2 text-[11px] text-slate-500 dark:text-slate-400">Moneda recibida</label>
              <select className={`${INPUT} mt-1`} value={monedaDestino} onChange={(e) => setMonedaDestino(e.target.value)} data-testid="movement-moneda-destino">
                <option value="USD">$ (Dólar)</option>
                <option value="VES">Bs (Bolívar)</option>
              </select>
            </div>
          </div>
        ) : ambiguous ? (
          <p className="font-mono2 text-[11px] text-amber-600 dark:text-amber-400 mb-3">No pude deducirlo. Elige una categoría o indica si es ingreso o egreso.</p>
        ) : guessed && !categoria ? (
          <p className="font-mono2 text-[11px] text-slate-400 mb-3">Detectado automáticamente según el concepto. Puedes cambiarlo.</p>
        ) : <div className="mb-3" />}

        <label className="font-mono2 text-[11px] text-slate-500 dark:text-slate-400">Fecha</label>
        <input type="date" className={`${INPUT} mt-1`} value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="movement-fecha" />
        <p className="font-mono2 text-[10px] text-slate-400 mt-1 mb-5">Puedes elegir cualquier fecha, incluso de años anteriores (2025, etc.). El año se creará solo.</p>

        <button onClick={submit} className={`${BTN_PRIMARY} w-full`} data-testid="movement-save">
          {initial ? "Guardar cambios" : "Agregar movimiento"}
        </button>
      </div>
    </div>
  );
}
